import React, { useState, useCallback, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    mediaQueries, colors, space, borders, buttons, radii
} from 'style/config';
import SwatchButton from 'components/ProductPage/Swatches/SwatchButton';
import { Box, Flex } from 'components/ui';
import Icon from 'components/Icon';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import ProductTileSwatchesEN from 'components/Catalog/ProductGrid/ProductTile/ProductTileSwatches/locales/ProductTileSwatches_en_US.js';
import ProductTileSwatchesFR from 'components/Catalog/ProductGrid/ProductTile/ProductTileSwatches/locales/ProductTileSwatches_fr_CA.js';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(ProductTileSwatchesEN, ProductTileSwatchesFR);

const DEFAULT_MAX_VISIBLE = 8;
const RECTANGLE_VERTICAL_MAX_VISIBLE = 4;
const CHEVRON_THRESHOLD = 10;
const SWATCH_GAP = 0;
const MOBILE_SWATCH_GAP = 4;
const VERTICAL_MOBILE_GAP = 6;
const TRACK_GAP = 0;
const SWATCH_IMG_SIZE = 22;
const SWATCH_BUTTON_INSET = 4; // To keep the swatch images aligned regardless of padding and border when the swatch is active
const SWATCH_BUTTON_SIZE = SWATCH_IMG_SIZE + SWATCH_BUTTON_INSET * 2;
const DESKTOP_SWATCH_GAP = 2;
const VERTICAL_CHEVRON_RIGHT = 26;
const VERTICAL_CHEVRON_TOP = 7;
const VERTICAL_EXPANDED_MAX_HEIGHT = 94; // Enough space to show three rows of swatches
const VERTICAL_EXPANDED_MAX_HEIGHT_MOBILE = 102; // Enough space to show three rows of swatches on mobile
const DIRECTION = { PREV: 'prev', NEXT: 'next' };
const SWATCH_BUTTON_TYPE = 'circle';
const RECTANGLE_SWATCH_IMG_WIDTH = 52;

export const LAYOUT = { HORIZONTAL: 'horizontal', VERTICAL: 'vertical' };

const ProductTileSwatches = ({
    swatchSelectors = Empty.Array,
    activeHeroImage,
    onSwatchSelect,
    onSwatchHover,
    layout = LAYOUT.HORIZONTAL,
    maxVisible = DEFAULT_MAX_VISIBLE,
    swatchType = SWATCH_BUTTON_TYPE
}) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [direction, setDirection] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const showChevrons = layout === LAYOUT.HORIZONTAL && swatchSelectors.length > CHEVRON_THRESHOLD;
    const totalPages = Math.ceil(swatchSelectors.length / maxVisible);

    useEffect(() => {
        setCurrentPage(prev => Math.min(prev, Math.max(0, totalPages - 1)));
    }, [totalPages]);
    const canGoPrev = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;

    const paginatedSwatches = showChevrons ? swatchSelectors.slice(currentPage * maxVisible, (currentPage + 1) * maxVisible) : swatchSelectors;

    const verticalMaxVisible = swatchType === 'rectangle' ? RECTANGLE_VERTICAL_MAX_VISIBLE : maxVisible;
    const overflowCount = swatchSelectors.length - verticalMaxVisible;

    const handleClick = useCallback(
        heroImage => e => {
            e.preventDefault();
            e.stopPropagation();
            onSwatchSelect(heroImage);
        },
        [onSwatchSelect]
    );

    const handleMouseEnter = useCallback(heroImage => () => onSwatchHover?.(heroImage), [onSwatchHover]);

    const handleMouseLeave = useCallback(() => onSwatchHover?.(null), [onSwatchHover]);

    const toggleExpand = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(prev => !prev);
    }, []);

    const handlePrev = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setDirection(DIRECTION.PREV);
        setCurrentPage(prev => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback(
        e => {
            e.preventDefault();
            e.stopPropagation();
            setDirection(DIRECTION.NEXT);
            setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
        },
        [totalPages]
    );

    const buildSwatchList = (swatches, offset = 0) =>
        swatches.map((swatch, index) => (
            <SwatchButton
                key={swatch.swatch}
                type={swatchType}
                isActive={swatch.heroImage === activeHeroImage}
                isImage={true}
                onClick={handleClick(swatch.heroImage)}
                onMouseEnter={handleMouseEnter(swatch.heroImage)}
                aria-label={getText('shade', [offset + index + 1])}
                css={[styles.swatchButton, styles.swatchButtonByType[swatchType]]}
            >
                <Box
                    is='img'
                    src={swatch.swatch}
                    alt=''
                    loading='lazy'
                    css={[styles.swatchImg, styles.swatchImgByType[swatchType]]}
                />
            </SwatchButton>
        ));

    if (layout === LAYOUT.VERTICAL) {
        return (
            <Box
                css={[styles.verticalWrapper, overflowCount > 0 && styles.verticalWrapperWithChevron]}
                onMouseLeave={handleMouseLeave}
            >
                <Flex css={[styles.verticalContainer, isExpanded && styles.verticalContainerExpanded]}>
                    {buildSwatchList(isExpanded ? swatchSelectors : swatchSelectors.slice(0, verticalMaxVisible))}
                </Flex>
                {overflowCount > 0 && (
                    <Box
                        is='button'
                        type='button'
                        css={[styles.chevronButton, styles.chevronActive, styles.verticalChevronButton]}
                        onClick={toggleExpand}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? getText('showFewerShades') : getText('showMoreShades', [overflowCount])}
                    >
                        <Icon name={isExpanded ? 'chevronUp' : 'chevronDown'} />
                    </Box>
                )}
            </Box>
        );
    }

    if (showChevrons) {
        return (
            <Box
                css={styles.chevronWrapper}
                onMouseLeave={handleMouseLeave}
            >
                {/* Mobile: scrollable row - no chevrons */}
                <Flex css={styles.mobileScrollRow}>{buildSwatchList(swatchSelectors)}</Flex>
                {/* Desktop: paginated row with chevrons */}
                <Flex css={styles.desktopChevronRow}>
                    <Box
                        is='button'
                        type='button'
                        disabled={!canGoPrev}
                        css={[styles.chevronButton, canGoPrev ? styles.chevronActive : styles.chevronInactive]}
                        onClick={handlePrev}
                        aria-label={getText('previousSwatches')}
                    >
                        <Icon name='chevronLeft' />
                    </Box>
                    <Flex
                        key={currentPage}
                        css={[
                            styles.swatchTrack,
                            direction === DIRECTION.NEXT && styles.slideFromRight,
                            direction === DIRECTION.PREV && styles.slideFromLeft
                        ]}
                    >
                        {buildSwatchList(paginatedSwatches, currentPage * maxVisible)}
                    </Flex>
                    <Box
                        is='button'
                        type='button'
                        disabled={!canGoNext}
                        css={[styles.chevronButton, canGoNext ? styles.chevronActive : styles.chevronInactive]}
                        onClick={handleNext}
                        aria-label={getText('nextSwatches')}
                    >
                        <Icon name='chevronRight' />
                    </Box>
                </Flex>
            </Box>
        );
    }

    return (
        <Flex
            css={styles.horizontalContainer}
            onMouseLeave={handleMouseLeave}
        >
            {buildSwatchList(swatchSelectors)}
        </Flex>
    );
};

const styles = {
    swatchButton: {
        cursor: 'pointer',
        scrollSnapAlign: 'start',
        flexShrink: 0
    },
    swatchButtonByType: {
        square: { marginRight: 0, marginTop: 0, borderRadius: radii[2] },
        rectangle: { borderRadius: radii[2] }
        // No extra styles needed for circle
    },
    horizontalContainer: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: SWATCH_GAP,
        marginLeft: -SWATCH_BUTTON_INSET,
        paddingTop: space[1],
        paddingBottom: space[1],
        overflow: 'hidden',
        [mediaQueries.xsMax]: {
            gap: MOBILE_SWATCH_GAP,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': { display: 'none' } // Safari (iOS) still requires webkit prefix
        }
    },
    chevronWrapper: {
        paddingTop: space[1],
        paddingBottom: space[1]
    },
    mobileScrollRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: MOBILE_SWATCH_GAP,
        marginLeft: -SWATCH_BUTTON_INSET,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': { display: 'none' },
        [mediaQueries.sm]: {
            display: 'none'
        }
    },
    desktopChevronRow: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'flex',
            alignItems: 'center',
            gap: DESKTOP_SWATCH_GAP,
            width: '100%',
            overflowX: 'visible',
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': { display: 'none' }
        }
    },
    swatchTrack: {
        display: 'flex',
        alignItems: 'center',
        gap: TRACK_GAP,
        flex: 1,
        overflow: 'hidden'
    },
    verticalWrapper: {
        position: 'relative',
        marginLeft: -SWATCH_BUTTON_INSET,
        paddingTop: space[1]
    },
    verticalWrapperWithChevron: {
        paddingRight: VERTICAL_CHEVRON_RIGHT
    },
    verticalContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: VERTICAL_MOBILE_GAP,
        overflowY: 'hidden',
        [mediaQueries.xsMax]: {
            maxHeight: SWATCH_BUTTON_SIZE
        },
        [mediaQueries.sm]: {
            gap: DESKTOP_SWATCH_GAP
        }
    },
    verticalContainerExpanded: {
        maxHeight: VERTICAL_EXPANDED_MAX_HEIGHT,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        '::-webkit-scrollbar-track': {
            marginTop: SWATCH_IMG_SIZE + space[1]
        },
        [mediaQueries.xsMax]: {
            maxHeight: VERTICAL_EXPANDED_MAX_HEIGHT_MOBILE
        }
    },
    verticalChevronButton: {
        position: 'absolute',
        top: VERTICAL_CHEVRON_TOP,
        right: 0
    },
    swatchImg: {
        width: SWATCH_IMG_SIZE,
        height: SWATCH_IMG_SIZE,
        display: 'block'
    },
    swatchImgByType: {
        circle: { borderRadius: '50%' },
        square: { borderRadius: radii[2] },
        rectangle: { borderRadius: radii[2], width: RECTANGLE_SWATCH_IMG_WIDTH }
    },
    slideFromRight: {
        animation: 'slideFromRight 0.2s ease-out',
        '@keyframes slideFromRight': {
            from: { transform: 'translateX(12px)', opacity: 0 },
            to: { transform: 'translateX(0)', opacity: 1 }
        }
    },
    slideFromLeft: {
        animation: 'slideFromLeft 0.2s ease-out',
        '@keyframes slideFromLeft': {
            from: { transform: 'translateX(-12px)', opacity: 0 },
            to: { transform: 'translateX(0)', opacity: 1 }
        }
    },
    chevronButton: {
        width: buttons.HEIGHT_XS,
        height: buttons.HEIGHT_XS,
        borderRadius: '50%',
        border: `${borders[1]} ${colors.midGray}`,
        background: colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        padding: 0
    },
    chevronActive: {
        color: colors.black,
        cursor: 'pointer'
    },
    chevronInactive: {
        color: colors.midGray,
        cursor: 'default'
    }
};

ProductTileSwatches.shouldUpdatePropsOn = ['activeHeroImage', 'swatchSelectors'];

export default wrapFunctionalComponent(ProductTileSwatches, 'ProductTileSwatches');
